import { Grid } from "@mui/material";
import { useState, useMemo } from "react";
import { FaMapMarkerAlt, FaSearch } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import styles from "./FilterDrawer.module.css";

const FilterDrawer = ({
  filterOptions,
  selectedFilters,
  setSelectedFilters,
  isOpen,
  onClose,
  updateResults,
  categoryMap,
  fetchJobs,
}) => {
  const [addingNewOption, setAddingNewOption] = useState(null);
  const [newOptionValue, setNewOptionValue] = useState("");

  // 🔹 Added: Local search query for companies
  const [companySearch, setCompanySearch] = useState("");

  const id = useParams();
  const navigate = useHistory();

  const handleSelect = (category, value) => {
    const apiKey = categoryMap[category] || category;
    const filterType = filterOptions[category]?.type || "checkbox";

    setSelectedFilters((prev) => {
      const updated = { ...prev };
      if (filterType === "radio") {
        updated[apiKey] = value;
      } else if (filterType === "checkbox") {
        updated[apiKey] = updated[apiKey] ? [...updated[apiKey]] : [];
        if (updated[apiKey].includes(value)) {
          updated[apiKey] = updated[apiKey].filter((item) => item !== value);
        } else {
          updated[apiKey].push(value);
        }
      }
      return updated;
    });
  };

  const handleInputChange = (category, value) => {
    const apiKey = categoryMap?.[category] || category;
    setSelectedFilters((prev) => ({
      ...prev,
      [apiKey]: value,
    }));
  };

  const handleApplyFilters = () => {
    updateResults(selectedFilters);
    onClose();
  };

  const isFilterSelected = (category, value) => {
    const mappedCategory = categoryMap[category] || category;
    if (!selectedFilters[mappedCategory]) return false;

    if (Array.isArray(selectedFilters[mappedCategory])) {
      return selectedFilters[mappedCategory].includes(value);
    }
    return selectedFilters[mappedCategory] === value;
  };

  const getInputType = (category) => {
    return filterOptions[category]?.type || "checkbox";
  };

  const handleAddOptionClick = (category) => {
    setAddingNewOption(category);
  };

  const handleAddNewOption = (category) => {
    if (newOptionValue.trim() === "") return;
    const apiKey = categoryMap[category] || category;
    setSelectedFilters((prev) => {
      const updated = { ...prev };
      updated[apiKey] = updated[apiKey] ? [...updated[apiKey]] : [];
      updated[apiKey].push(newOptionValue.trim());
      return updated;
    });
    setNewOptionValue("");
    setAddingNewOption(null);
  };

  // 🔹 Added: Filter companies based on search query
  const getFilteredCompanies = (companies) => {
    if (!companySearch.trim()) return companies.slice(0, 20); // show first 20 by default
    return companies.filter((c) =>
      (c.label || c.value || c)
        .toString()
        .toLowerCase()
        .includes(companySearch.toLowerCase())
    );
  };

  return (
    <div className={`${styles.filterDrawer} ${isOpen ? styles.open : ""}`}>
      <div className={styles.drawerHeader}>
        <h3>Filter only Jobs</h3>
        <button onClick={onClose} className={styles.closeButton}>
          ✖
        </button>
      </div>

      <div className={styles.drawerContent}>
        {Object.entries(filterOptions).map(([category, filterData]) => {
          // 🔹 Special handling for "Company"
          const isCompanySection = category.toLowerCase() === "company";
          const options =
            isCompanySection && Array.isArray(filterData.options)
              ? getFilteredCompanies(filterData.options)
              : filterData.options;

          return (
            <div key={category} className={styles.filterCategory}>
              <div className={styles.filterHeaderlabel}>
                {filterData.label || category.replace(/([A-Z])/g, " $1").trim()}
              </div>

              <Grid
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {filterData.type === "input" ? (
                  <div className={styles.inputWrapper}>
                    {category === "searchjobs" && (
                      <FaSearch className={styles.inputIcon} />
                    )}
                    {category === "location" && (
                      <FaMapMarkerAlt className={styles.inputIcon} />
                    )}
                    <input
                      type="text"
                      className={styles.textInput}
                      placeholder={filterData.placeholder || filterData.label}
                      value={
                        selectedFilters[categoryMap?.[category] || category] ||
                        ""
                      }
                      onChange={(e) =>
                        handleInputChange(category, e.target.value)
                      }
                    />
                  </div>
                ) : (
                  <>
                    {/* 🔹 Added: Company search bar */}
                    {isCompanySection && (
                      <div className={styles.companySearchWrapper}>
                        <FaSearch className={styles.inputIcon} />
                        <input
                          type="text"
                          placeholder="Search companies..."
                          value={companySearch}
                          onChange={(e) => setCompanySearch(e.target.value)}
                          className={styles.textInput}
                          style={{
                            marginBottom: "10px",
                            width: "100%",
                            paddingLeft: "30px",
                          }}
                        />
                      </div>
                    )}

                    {options.map((option) => (
                      <Grid
                        key={option.value || option}
                        sx={{
                          width: "50%",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <label className={styles.filterOption}>
                          <input
                            type={getInputType(category)}
                            name={category}
                            checked={isFilterSelected(
                              category,
                              option.value || option
                            )}
                            onChange={() =>
                              handleSelect(category, option.value || option)
                            }
                          />
                          <div>{option.label || option}</div>
                        </label>
                      </Grid>
                    ))}

                    {/* Add new option section (unchanged) */}
                    {addingNewOption === category ? (
                      <div className={styles.addOptionInputContainer}>
                        <input
                          type="text"
                          placeholder={`Enter a new ${filterData.label.toLowerCase()}`}
                          value={newOptionValue}
                          onChange={(e) => setNewOptionValue(e.target.value)}
                          className={styles.addOptionInput}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleAddNewOption(category);
                            }
                          }}
                        />
                        <button
                          className={styles.addOptionButton}
                          onClick={() => handleAddNewOption(category)}
                        >
                          Add
                        </button>
                      </div>
                    ) : (
                      (category === "industry" ||
                        category === "jobFunction") && (
                        <button
                          className={styles.addOptionLink}
                          onClick={() => handleAddOptionClick(category)}
                        >
                          + Add a {filterData.label.toLowerCase()}
                        </button>
                      )
                    )}
                  </>
                )}
              </Grid>
              <div className={styles.horzontalDivider}></div>
            </div>
          );
        })}
      </div>

      <div className={styles.drawerFooter}>
        <button
          onClick={() => {
            if (id) {
              navigate.push("/jobs", { replace: true });
            }
            setSelectedFilters({});
            fetchJobs();
          }}
          className={styles.resetButton}
        >
          Reset
        </button>
        <button onClick={handleApplyFilters} className={styles.applyButton}>
          Show results
        </button>
      </div>
    </div>
  );
};

export default FilterDrawer;
